import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../Toast/ToastContext";
import ApiUrl from "./../Environment/ApiUrl";
import "../../assets/css/Loader.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import "../../assets/css/Chit.css";

function ChitList() {
  const toast = useToast();
  const navigate = useNavigate();
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [infoModal, setInfoModal] = useState(false);
  const [deleteShowModals, setDeleteShowModals] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedChitId, setSelectedChitId] = useState(null);
  const [actionType, setActionType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Data states
  const [responseData, setResponseData] = useState([]);
  const [generate, setGenerate] = useState([]);
  const [chitCreation, setChitCreation] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [userdata, setUserdata] = useState([]);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [isManagerLocked, setIsManagerLocked] = useState(false);
  const [isStaffLocked, setIsStaffLocked] = useState(false);
  const [usedInputNumbers, setUsedInputNumbers] = useState({});
  const [existingChitIds, setExistingChitIds] = useState([]);
  const [codeDropdownOpen, setCodeDropdownOpen] = useState(false);
  const [updateCodeDropdownOpen, setUpdateCodeDropdownOpen] = useState(false);

  // Tamil translation states
  const [isTranslating, setIsTranslating] = useState(false);

  // Tamil translation function
  const translateToTamil = async (englishText) => {
    if (!englishText || englishText.trim().length < 2) return "";
    setIsTranslating(true);
    const apis = [
      async () => {
        const encoded = encodeURIComponent(englishText.trim());
        const res = await fetch(
          `https://api.mymemory.translated.net/get?q=${encoded}&langpair=en|ta`,
        );
        const data = await res.json();
        const translated = data?.responseData?.translatedText;
        if (
          translated &&
          translated.toLowerCase() !== englishText.toLowerCase()
        ) {
          return translated;
        }
        return null;
      },
      async () => {
        const encoded = encodeURIComponent(englishText.trim());
        const res = await fetch(`https://lingva.ml/api/v1/en/ta/${encoded}`);
        if (res.ok) {
          const data = await res.json();
          if (data.translation && data.translation !== englishText) {
            return data.translation;
          }
        }
        return null;
      },
      async () => {
        const res = await fetch("https://libretranslate.com/translate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            q: englishText.trim(),
            source: "en",
            target: "ta",
            format: "text",
          }),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.translatedText) {
            return data.translatedText;
          }
        }
        return null;
      },
    ];
    for (const api of apis) {
      try {
        const result = await api();
        if (result) {
          setIsTranslating(false);
          return result;
        }
      } catch (error) {
        console.warn("Translation API failed:", error);
        continue;
      }
    }
    setIsTranslating(false);
    return "";
  };
  // Only one code is ever "open" at a time — the first letter that isn't full
  const getActiveCode = () => {
    for (let i = 0; i < 26; i++) {
      const letter = String.fromCharCode(65 + i);
      const usedCount = usedInputNumbers[letter]?.length || 0;
      if (usedCount < 500) return letter;
    }
    return null; // all 26 codes are full
  };
  // ── Get Next Available Input Number for a Code ─────────────────────────
  const getNextAvailableInputNumber = (codeLetter) => {
    const existingIds = existingChitIds.filter((id) =>
      id.startsWith(`C${codeLetter}`),
    );

    if (existingIds.length === 0) {
      return 1;
    }

    const numbers = existingIds
      .map((id) => {
        const match = id.match(/^C([A-Z])(\d+)-/);
        return match ? parseInt(match[2]) : 0;
      })
      .filter((num) => num > 0);

    if (numbers.length === 0) return 1;

    numbers.sort((a, b) => a - b);
    let nextNumber = 1;
    for (let i = 0; i < numbers.length; i++) {
      if (numbers[i] > nextNumber) break;
      if (numbers[i] === nextNumber) {
        nextNumber++;
      }
    }
    return nextNumber;
  };

  // ── Generate Chit ID ─────────────────────────────────────────────────────
  const generateChitId = (code, inputNumber, amount) => {
    const cleanAmount = amount ? amount.toString().replace(/[^\d]/g, "") : "";
    return `C${code}${inputNumber}-${cleanAmount}`;
  };

  // ── Check if Chit ID already exists ─────────────────────────────────────
  const isChitIdExists = (code, inputNumber, amount) => {
    const generatedId = generateChitId(code, inputNumber, amount);
    return existingChitIds.includes(generatedId);
  };

  const handleprint = (id) => {
    navigate(`/chit-bill/${id}`);
  };

  // Fetch chit data
  const fetchData = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/chit_list.php`);
      const data = (response.data?.data || []).slice().reverse();
      setResponseData(data);

      // Build existing chit IDs for uniqueness check
      const ids = data.map((item) => item.chit_id).filter(Boolean);
      setExistingChitIds(ids);

      // Build used input numbers per code
      const usedNumbers = {};
      ids.forEach((id) => {
        const match = id.match(/^C([A-Z])(\d+)-/);
        if (match) {
          const codeLetter = match[1];
          const number = parseInt(match[2]);
          if (!usedNumbers[codeLetter]) {
            usedNumbers[codeLetter] = [];
          }
          usedNumbers[codeLetter].push(number);
        }
      });
      setUsedInputNumbers(usedNumbers);
    } catch (error) {
      console.error("Error fetching data:", error.message);
      toast.error("Failed to fetch data", 3000, "topRight");
    }
  };

  // Fetch chit creation data
  const fetchDataChit = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/chit.php`);
      setChitCreation(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching chit creation data:", error.message);
      toast.error("Failed to fetch chit data", 3000, "topRight");
    }
  };

  // Fetch generated customer ID
  const fetchDataGenerated = async () => {
    try {
      const response = await axios.get(
        `${ApiUrl}/Api/chit_list.php/generate-customer-id`,
      );
      const chitId = response.data?.customer_id;
      setGenerate(chitId);
      setAddData((prevData) => ({
        ...prevData,
        customer_id: chitId,
      }));
    } catch (error) {
      console.error("Error fetching generated data:", error.message);
      toast.error("Failed to fetch generated data", 3000, "topRight");
    }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/notification.php`);
      setNotifications(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching notifications:", error.message);
      toast.error("Failed to fetch notifications", 3000, "topRight");
    }
  };

  // Check if a notification is accepted for the action
  const checkNotificationStatus = (chitId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.some(
      (notif) =>
        notif.table_id === chitId.toString() &&
        notif.button_value === buttonValue &&
        notif.status === "accepted" &&
        notif.employe_id === userId &&
        notif.viewed === "false",
    );
  };

  // Check if a notification is pending
  const hasPendingNotification = (chitId, buttonValue) => {
    const userId = localStorage.getItem("username");
    return notifications.some(
      (notif) =>
        notif.table_id === chitId.toString() &&
        notif.button_value === buttonValue &&
        notif.status === "requested" &&
        notif.employe_id === userId &&
        notif.viewed === "false",
    );
  };

  // Open notification modal
  const openNotificationModal = (id, action) => {
    setSelectedChitId(id);
    setActionType(action);
    setShowNotificationModal(true);
  };

  // Close notification modal
  const closeNotificationModal = () => {
    setShowNotificationModal(false);
    setSelectedChitId(null);
    setActionType("");
    setIsSubmitting(false);
  };

  // Handle notification submission
  const handleNotificationSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSubmitting) return;
    setIsSubmitting(true);
    if (hasPendingNotification(selectedChitId, actionType)) {
      toast.warning(
        "A pending request for this action already exists!",
        3000,
        "topRight",
      );
      closeNotificationModal();
      return;
    }
    try {
      const userId = localStorage.getItem("username");
      const currentTime = new Date()
        .toISOString()
        .slice(0, 19)
        .replace("T", " ");
      const formData = {
        table_name: "chit_list",
        table_id: selectedChitId,
        employe_id: userId || "",
        status: "requested",
        button_value: actionType,
        time: currentTime,
        viewed: "false",
        created_date: currentTime,
        updated_by: userId || "",
        updated_date: currentTime,
      };
      await axios.post(`${ApiUrl}/Api/notification.php`, formData, {
        headers: { "Content-Type": "application/json" },
      });
      toast.success(
        `${actionType} request sent successfully!`,
        3000,
        "topRight",
      );
      closeNotificationModal();
      fetchNotifications();
    } catch (error) {
      console.error("Error submitting notification:", error.message);
      toast.error(
        "Error submitting notification. Please try again.",
        3000,
        "topRight",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle update chit
  const handleUpdate = (id, data) => {
    const userRole = localStorage.getItem("role");
    // if (userRole === "Employee") {
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(id, "Edit")) {
        proceedWithUpdate(id, data);
      } else {
        openNotificationModal(id, "Edit");
      }
    } else {
      proceedWithUpdate(id, data);
    }
  };

  const fetchCompleteChitDetails = async (chitId) => {
    try {
      const response = await axios.get(`${ApiUrl}/Api/chit_list.php/${chitId}`);
      return response.data?.data;
    } catch (error) {
      console.error("Error fetching complete chit details:", error);
      return null;
    }
  };

  const proceedWithUpdate = async (id, data) => {
    setUpdateId(id);

    let completeData = data;
    if (!data.aadhar_no) {
      const fullData = await fetchCompleteChitDetails(id);
      if (fullData) {
        completeData = { ...data, ...fullData };
      }
    }

    let formattedStartDate = "";
    let formattedEndDate = "";

    if (completeData.start_date) {
      const startDate = new Date(completeData.start_date);
      if (!isNaN(startDate.getTime())) {
        formattedStartDate = startDate.toISOString().split("T")[0];
      }
    }
    if (completeData.end_date) {
      const endDate = new Date(completeData.end_date);
      if (!isNaN(endDate.getTime())) {
        formattedEndDate = endDate.toISOString().split("T")[0];
      }
    }

    let extractedCode = completeData.code || "";
    let extractedInputNumber = completeData.input_number || "";

    if (!extractedCode && completeData.chit_id) {
      const chitId = completeData.chit_id;
      const beforeDash = chitId.split("-")[0];
      const afterC = beforeDash.substring(1);
      if (afterC && afterC.length > 0) {
        extractedCode = afterC[0];
        extractedInputNumber = afterC.substring(1);
      }
    }

    const existingManagerName =
      completeData.manager_name || completeData.manager || "";
    const existingStaff = completeData.staff || "";

    let managerIdForSelect = "";
    if (existingManagerName) {
      const managerObj = userdata.find(
        (u) =>
          (u.name === existingManagerName ||
            u.user_id === existingManagerName) &&
          (u.role === "Manager" || u.role === "manager"),
      );
      if (managerObj) {
        managerIdForSelect = managerObj.id.toString();
      }
    }

    setSelectedManager(managerIdForSelect);
    setSelectedStaff(existingStaff);

    const allStaff = userdata.filter(
      (u) => u.role === "Employee" || u.role === "employee",
    );
    setStaffList(allStaff);

    setUpdateModal(true);
    setUpdateData({
      chit_id: completeData.chit_id,
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      customer_id: completeData.customer_id,
      mobile: completeData.mobile,
      amount: completeData.amount,
      address: completeData.address,
      name: completeData.name,
      name_tamil: completeData.name_tamil || "",
      duration: completeData.duration,
      status: completeData.status || "Open",
      created_by: completeData.created_by || "",
      created_date: completeData.created_date || "",
      updated_by: completeData.updated_by || "",
      updated_date: completeData.updated_date || "",
      aadhar_no: completeData.aadhar_no || "",
      code: extractedCode,
      input_number: extractedInputNumber,
      manager: managerIdForSelect,
      manager_name: existingManagerName,
      staff: existingStaff,
    });
  };

  // Handle delete chit
  const handleOpenModal = (id, customer_name) => {
    const userRole = localStorage.getItem("role");
    // if (userRole === "Employee") {
    if (userRole === "Employee" || userRole === "Manager") {
      if (checkNotificationStatus(id, "Delete")) {
        setDeleteId(id);
        setDeleteName(customer_name);
        setDeleteShowModals(true);
      } else {
        openNotificationModal(id, "Delete");
      }
    } else {
      setDeleteId(id);
      setDeleteName(customer_name);
      setDeleteShowModals(true);
    }
  };

  const handleCloseModal = () => {
    setDeleteShowModals(false);
    setDeleteId("");
    setDeleteName("");
  };

  const handleDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(`${ApiUrl}/Api/chit_list.php/${getDeleteId}`);
      const notification = notifications.find(
        (notif) =>
          notif.table_id === getDeleteId.toString() &&
          notif.button_value === "Delete" &&
          notif.viewed === "false",
      );
      if (notification) {
        const currentTime = new Date()
          .toISOString()
          .slice(0, 19)
          .replace("T", " ");
        const notificationPayload = {
          id: notification.id,
          viewed: "true",
          updated_by: localStorage.getItem("username"),
          updated_date: currentTime,
          table_name: notification.table_name,
          table_id: notification.table_id,
          employe_id: notification.employe_id,
          status: notification.status,
          button_value: notification.button_value,
          time: notification.time,
          created_date: notification.created_date,
        };
        await axios.put(
          `${ApiUrl}/Api/notification.php/${notification.id}`,
          notificationPayload,
          { headers: { "Content-Type": "application/json" } },
        );
      }
      toast.success("Chit deleted successfully!", 3000, "topRight");
      handleCloseModal();
      fetchData();
      fetchNotifications();
    } catch (error) {
      console.error("Error deleting data:", error.message);
      toast.error("Failed to delete data", 3000, "topRight");
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const openModelHistory = (index) => {
    const chit = currentItems[index];
    setSelectedChit(chit);
    const totalPaid = chit.payment_history.reduce(
      (sum, payment) => sum + parseFloat(payment.amount || 0),
      0,
    );
    const total = parseFloat(chit.amount || 0) * parseInt(chit.duration || 1);
    const balance = total - totalPaid;
    setPayment({
      chit_id: chit.chit_id || "",
      customer_id: chit.customer_id || "",
      amount: "",
      payment_type: "",
      payment_date: "",
      total: total.toFixed(2),
      paid: totalPaid.toFixed(2),
      balance: balance.toFixed(2),
    });
    setInfoModal(true);
  };

  const closeModal = () => setShowModal(false);
  const closePayment = () => setPaymentModal(false);
  const closePaymentTable = () => setInfoModal(false);
  const closeUpdateModal = () => setUpdateModal(false);

  const handleChitChange = (e) => {
    const selectedChitId = e.target.value;
    const selectedChit = responseData.find(
      (item) => item.chit_id === selectedChitId,
    );
    if (selectedChit) {
      const { totalPaid, totalBalance } = calculateOverallAmounts(selectedChit);
      const total =
        parseFloat(selectedChit.amount || 0) *
        parseInt(selectedChit.duration || 1);
      setSelectedChit(selectedChit);
      setPayment({
        ...payment,
        chit_id: selectedChitId,
        customer_id: selectedChit.customer_id,
        total: total.toFixed(2),
        paid: totalPaid.toFixed(2),
        balance: totalBalance.toFixed(2),
      });
    } else {
      setSelectedChit(null);
      setPayment({
        ...payment,
        chit_id: selectedChitId,
        customer_id: "",
        total: "",
        paid: "",
        balance: "",
      });
    }
  };

  const calculateOverallAmounts = (selectedChit) => {
    if (!selectedChit?.payment_history?.length) {
      return {
        totalPaid: 0,
        totalBalance:
          parseFloat(selectedChit?.amount || 0) *
          parseInt(selectedChit?.duration || 1),
      };
    }
    const total =
      parseFloat(selectedChit.amount || 0) *
      parseInt(selectedChit.duration || 1);
    const totalPaid = selectedChit.payment_history.reduce(
      (sum, payment) => sum + parseFloat(payment.paid || 0),
      0,
    );
    const totalBalance = total - totalPaid;
    return { totalPaid, totalBalance };
  };

  const Addpaymentmodal = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const newAmount = parseFloat(payment.amount);
      if (isNaN(newAmount) || newAmount <= 0) {
        toast.error("Invalid payment amount", 3000, "topRight");
        return;
      }
      const currentTotalPaid =
        selectedChit?.payment_history?.reduce(
          (sum, p) => sum + parseFloat(p.paid || 0),
          0,
        ) || 0;
      const newTotalPaid = currentTotalPaid + newAmount;
      const total =
        parseFloat(selectedChit?.amount || payment.total || 0) *
        parseInt(selectedChit?.duration || 1);
      const newBalance = total - newTotalPaid;
      const displayBalance = newBalance < 0 ? 0 : newBalance;
      const formData = {
        chit_id: payment.chit_id,
        amount: newAmount.toFixed(2),
        payment_type: payment.payment_type,
        payment_date: payment.payment_date,
        customer_id: payment.customer_id,
        total: total.toFixed(2),
        paid: newAmount.toFixed(2),
        balance: displayBalance.toFixed(2),
        excess_amount:
          newBalance < 0 ? Math.abs(newBalance).toFixed(2) : "0.00",
        created_by: "",
        created_date: "",
      };
      await axios.post(
        `${ApiUrl}/Api/chit_list_payement_history.php`,
        formData,
      );
      setResponseData((prevData) =>
        prevData.map((item) =>
          item.chit_id === payment.chit_id
            ? {
                ...item,
                payment_history: [
                  ...item.payment_history,
                  {
                    amount: newAmount.toFixed(2),
                    paid: newAmount.toFixed(2),
                    balance: displayBalance.toFixed(2),
                    excess_amount:
                      newBalance < 0 ? Math.abs(newBalance).toFixed(2) : "0.00",
                    payment_type: payment.payment_type,
                    payment_date: payment.payment_date,
                  },
                ],
              }
            : item,
        ),
      );
      if (selectedChit && selectedChit.chit_id === payment.chit_id) {
        setSelectedChit((prev) => ({
          ...prev,
          payment_history: [
            ...prev.payment_history,
            {
              amount: newAmount.toFixed(2),
              paid: newAmount.toFixed(2),
              balance: displayBalance.toFixed(2),
              excess_amount:
                newBalance < 0 ? Math.abs(newBalance).toFixed(2) : "0.00",
              payment_type: payment.payment_type,
              payment_date: payment.payment_date,
            },
          ],
        }));
      }
      setPayment({
        ...payment,
        amount: "",
        payment_type: "",
        payment_date: "",
      });
      setPaymentModal(false);
      toast.success("Payment added successfully!", 3000, "topRight");
      fetchData();
    } catch (error) {
      console.error("Error adding payment:", error.message);
      toast.error("Error adding payment: " + error.message, 3000, "topRight");
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  const AddPayment = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const newAmount = parseFloat(payment.amount);
      if (isNaN(newAmount) || newAmount <= 0) {
        toast.error("Invalid payment amount", 3000, "topRight");
        return;
      }
      const currentTotalPaid =
        selectedChit?.payment_history?.reduce(
          (sum, p) => sum + parseFloat(p.paid || 0),
          0,
        ) || 0;
      const newTotalPaid = currentTotalPaid + newAmount;
      const total =
        parseFloat(selectedChit?.amount || payment.total || 0) *
        parseInt(selectedChit?.duration || 1);
      const newBalance = total - newTotalPaid;
      const displayBalance2 = newBalance < 0 ? 0 : newBalance;
      const formData = {
        chit_id: payment.chit_id,
        amount: newAmount.toFixed(2),
        payment_type: payment.payment_type,
        payment_date: payment.payment_date,
        customer_id: payment.customer_id,
        total: total.toFixed(2),
        paid: newAmount.toFixed(2),
        balance: displayBalance2.toFixed(2),
        excess_amount:
          newBalance < 0 ? Math.abs(newBalance).toFixed(2) : "0.00",
        created_by: "",
        created_date: "",
      };
      await axios.post(
        `${ApiUrl}/Api/chit_list_payement_history.php`,
        formData,
      );
      setResponseData((prevData) =>
        prevData.map((item) =>
          item.chit_id === payment.chit_id
            ? {
                ...item,
                payment_history: [
                  ...item.payment_history,
                  {
                    amount: newAmount.toFixed(2),
                    paid: newAmount.toFixed(2),
                    balance: displayBalance2.toFixed(2),
                    excess_amount:
                      newBalance < 0 ? Math.abs(newBalance).toFixed(2) : "0.00",
                    payment_type: payment.payment_type,
                    payment_date: payment.payment_date,
                  },
                ],
              }
            : item,
        ),
      );
      if (selectedChit && selectedChit.chit_id === payment.chit_id) {
        setSelectedChit((prev) => ({
          ...prev,
          payment_history: [
            ...prev.payment_history,
            {
              amount: newAmount.toFixed(2),
              paid: newAmount.toFixed(2),
              balance: displayBalance2.toFixed(2),
              excess_amount:
                newBalance < 0 ? Math.abs(newBalance).toFixed(2) : "0.00",
              payment_type: payment.payment_type,
              payment_date: payment.payment_date,
            },
          ],
        }));
      }
      setPayment({
        ...payment,
        amount: "",
        payment_type: "",
        payment_date: "",
      });
      setPaymentModal(false);
      setInfoModal(false);
      toast.success("Payment added successfully!", 3000, "topRight");
      fetchData();
    } catch (error) {
      console.error("Error adding payment:", error.message);
      toast.error("Error adding payment: " + error.message, 3000, "topRight");
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  // Form states
  const [addData, setAddData] = useState({
    chit_id: "",
    start_date: "",
    end_date: "",
    customer_id: "",
    mobile: "",
    amount: "",
    address: "",
    name: "",
    name_tamil: "",
    duration: "",
    status: "Open",
    created_by: "",
    created_date: "",
    updated_by: "",
    updated_date: "",
    aadhar_no: "",
    code: "",
    input_number: "",
    manager: "",
    manager_name: "",
    staff: "",
  });

  const [payment, setPayment] = useState({
    chit_id: "",
    amount: "",
    payment_type: "",
    payment_date: "",
    customer_id: "",
    total: "",
    paid: "",
    balance: "",
  });

  const [selectedChit, setSelectedChit] = useState(null);

  const [updateData, setUpdateData] = useState({
    chit_id: "",
    start_date: "",
    amount: "",
    end_date: "",
    customer_id: "",
    mobile: "",
    address: "",
    name: "",
    name_tamil: "",
    duration: "",
    status: "",
    created_by: "",
    created_date: "",
    updated_by: "",
    updated_date: "",
    aadhar_no: "",
    code: "",
    input_number: "",
  });

  const [updateId, setUpdateId] = useState("");
  const [getDeleteId, setDeleteId] = useState("");
  const [getDeleteName, setDeleteName] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAllItems, setShowAllItems] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Close Chit states
  const [closeChitModal, setCloseChitModal] = useState(false);
  const [closeChitData, setCloseChitData] = useState(null);
  const [closeReturnType, setCloseReturnType] = useState("cash");
  const [closeGoldWeight, setCloseGoldWeight] = useState("");
  const [closeLoading, setCloseLoading] = useState(false);
  const [todayGoldRate, setTodayGoldRate] = useState(0);
  const [todayPureGoldRate, setTodayPureGoldRate] = useState(0);
  const [todaySilverRate, setTodaySilverRate] = useState(0);
  const [closeSilverWeight, setCloseSilverWeight] = useState("");

  // Handle Add Chit form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate Aadhar Number
    if (!addData.aadhar_no || addData.aadhar_no.length !== 12) {
      toast.error(
        "Please enter a valid 12-digit Aadhar number",
        3000,
        "topRight",
      );
      return;
    }

    if (!addData.manager || !addData.staff) {
      toast.error("Please select Manager and Staff", 3000, "topRight");
      return;
    }

    if (!addData.code) {
      toast.error("Please select a Code", 3000, "topRight");
      return;
    }

    if (!addData.input_number) {
      toast.error("Please select an Input Number", 3000, "topRight");
      return;
    }

    // Check if Chit ID already exists
    if (isChitIdExists(addData.code, addData.input_number, addData.amount)) {
      toast.error(
        "This Chit ID already exists! Please use a different code or input number.",
        5000,
        "topRight",
      );
      return;
    }

    try {
      setLoading(true);
      const generatedChitId = generateChitId(
        addData.code,
        addData.input_number,
        addData.amount,
      );
      const formData = {
        chit_id: generatedChitId,
        start_date: addData.start_date,
        end_date: addData.end_date,
        mobile: addData.mobile,
        amount: addData.amount,
        address: addData.address,
        customer_id: addData.customer_id,
        name: addData.name,
        name_tamil: addData.name_tamil || "",
        duration: addData.duration,
        status: addData.status,
        aadhar_no: addData.aadhar_no,
        code: addData.code,
        input_number: addData.input_number,
        manager: addData.manager,
        manager_name: addData.manager_name,
        staff: addData.staff,
        created_by: "",
        created_date: "",
      };
      await axios.post(`${ApiUrl}/Api/chit_list.php`, formData);
      setShowModal(false);
      setAddData({
        chit_id: "",
        start_date: "",
        end_date: "",
        customer_id: generate,
        mobile: "",
        amount: "",
        address: "",
        name: "",
        name_tamil: "",
        duration: "",
        status: "Open",
        created_by: "",
        created_date: "",
        updated_by: "",
        updated_date: "",
        aadhar_no: "",
        code: "",
        input_number: "",
        manager: "",
        manager_name: "",
        staff: "",
      });
      toast.success("Added successfully!", 3000, "topRight");
      fetchData();
    } catch (error) {
      console.error("Error adding data:", error.message);
      toast.error("Error adding data: " + error.message, 3000, "topRight");
    } finally {
      setTimeout(() => setLoading(false), 1000);
    }
  };

  // Handle Update Chit form submission
  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (
      !updateData.chit_id ||
      !updateData.customer_id ||
      !updateData.name ||
      !updateData.start_date ||
      !updateData.mobile ||
      !updateData.address ||
      !updateData.status ||
      !updateData.amount ||
      !updateData.code ||
      !updateData.input_number ||
      !updateData.aadhar_no
    ) {
      toast.error("Please fill in all required fields.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    if (!/^\d{10}$/.test(updateData.mobile)) {
      toast.error("Mobile number must be exactly 10 digits.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    if (updateData.aadhar_no.length !== 12) {
      toast.error("Aadhar number must be exactly 12 digits.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    // Check if Chit ID already exists (excluding current)
    const generatedId = generateChitId(
      updateData.code,
      updateData.input_number,
      updateData.amount,
    );
    if (
      generatedId !== updateData.chit_id &&
      existingChitIds.includes(generatedId)
    ) {
      toast.error(
        "This Chit ID already exists! Please use a different code or input number.",
        5000,
        "topRight",
      );
      return;
    }

    try {
      setLoading(true);
      const generatedChitId = generateChitId(
        updateData.code,
        updateData.input_number,
        updateData.amount,
      );
      const formData = {
        chit_id: generatedChitId,
        start_date: updateData.start_date,
        end_date: updateData.end_date || null,
        customer_id: updateData.customer_id,
        mobile: updateData.mobile,
        amount: updateData.amount,
        address: updateData.address,
        name: updateData.name,
        name_tamil: updateData.name_tamil || "",
        duration: updateData.duration || null,
        status: updateData.status,
        aadhar_no: updateData.aadhar_no,
        code: updateData.code,
        input_number: updateData.input_number,
        manager: updateData.manager || selectedManager,
        staff: updateData.staff || selectedStaff,
        created_by: localStorage.getItem("username") || "",
        created_date: "",
        updated_by: localStorage.getItem("username") || "",
        updated_date: new Date().toISOString().slice(0, 19).replace("T", " "),
      };
      if (!updateId) throw new Error("Update ID is missing.");
      await axios.put(`${ApiUrl}/Api/chit_list.php/${updateId}`, formData, {
        headers: { "Content-Type": "application/json" },
      });
      if (Array.isArray(notifications)) {
        const notification = notifications.find(
          (notif) =>
            notif.table_id === updateId.toString() &&
            notif.button_value === "Edit" &&
            notif.viewed === "false",
        );
        if (notification) {
          const currentTime = new Date()
            .toISOString()
            .slice(0, 19)
            .replace("T", " ");
          const notificationPayload = {
            id: notification.id,
            viewed: "true",
            updated_by: localStorage.getItem("username") || "",
            updated_date: currentTime,
            table_name: notification.table_name,
            table_id: notification.table_id,
            employe_id: notification.employe_id,
            status: notification.status,
            button_value: notification.button_value,
            time: notification.time,
            created_date: notification.created_date,
          };
          await axios.put(
            `${ApiUrl}/Api/notification.php/${notification.id}`,
            notificationPayload,
            { headers: { "Content-Type": "application/json" } },
          );
        }
      }
      setUpdateModal(false);
      toast.success("Updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
      fetchData();
      fetchNotifications();
    } catch (error) {
      console.error("Error updating data:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Unknown error occurred";
      toast.error(`Error updating data: ${errorMessage}`, {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredData = responseData.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    const totalChitAmount =
      parseFloat(item.amount || 0) * parseInt(item.duration || 1);
    return (
      (item.chit_id && item.chit_id.toLowerCase().includes(searchLower)) ||
      (item.customer_id &&
        item.customer_id.toLowerCase().includes(searchLower)) ||
      (item.name && item.name.toLowerCase().includes(searchLower)) ||
      (item.mobile && item.mobile.toLowerCase().includes(searchLower)) ||
      (item.duration && item.duration.toLowerCase().includes(searchLower)) ||
      (item.status && item.status.toLowerCase().includes(searchLower)) ||
      totalChitAmount.toString().includes(searchLower)
    );
  });

  const totalItems = filteredData.length;
  const totalPages = showAllItems ? 1 : Math.ceil(totalItems / itemsPerPage);
  const currentItems = showAllItems
    ? filteredData
    : filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage,
      );

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === "All") {
      setShowAllItems(true);
      setCurrentPage(1);
    } else {
      setShowAllItems(false);
      setItemsPerPage(Number(value));
      setCurrentPage(1);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 2;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    if (startPage > 1) {
      pages.push(
        <li
          key={1}
          className={`page-item ${currentPage === 1 ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(1)}>
            1
          </button>
        </li>,
      );
      if (startPage > 2) {
        pages.push(
          <li key="start-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }
    }
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li
          key={i}
          className={`page-item ${currentPage === i ? "active" : ""}`}
        >
          <button className="page-link" onClick={() => handlePageChange(i)}>
            {i}
          </button>
        </li>,
      );
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <li key="end-ellipsis" className="page-item disabled">
            <span className="page-link">...</span>
          </li>,
        );
      }
      pages.push(
        <li
          key={totalPages}
          className={`page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <button
            className="page-link"
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>
        </li>,
      );
    }
    return pages;
  };

  const handlePrint = () => {
    try {
      const printWindow = window.open("", "_blank");
      printWindow.document.write(`
        <html>
          <head>
            <title>Chit List Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              h1 { text-align: center; color: #333; margin-bottom: 30px; }
              table { width: 100%; border-collapse: collapse; margin-top: 20px; }
              th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
              th { background-color: #f2f2f2; font-weight: bold; }
              tr:nth-child(even) { background-color: #f9f9f9; }
              .print-date { text-align: right; margin-bottom: 20px; font-size: 12px; }
              @media print { body { margin: 0; } .no-print { display: none; } }
            </style>
          </head>
          <body>
            <div class="print-date">Generated on: ${new Date().toLocaleString()}</div>
            <h1>Chit List Report</h1>
            <table>
              <thead>
                <tr>
                  <th>S.No</th><th>Chit ID</th><th>Customer Name</th>
                  <th>Mobile Number</th><th>Aadhar No</th><th>Total Chit Amount</th>
                  <th>Duration (Months)</th><th>Start Date</th><th>End Date</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                ${currentItems
                  .map((item, index) => {
                    const totalChitAmount =
                      parseFloat(item.amount || 0) *
                      parseInt(item.duration || 1);
                    const splitUpAmount = parseFloat(item.amount || 0).toFixed(
                      2,
                    );
                    return `
                  <tr>
                    <td>${(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>${item.chit_id || ""}</td>
                    <td>${item.name || ""}${item.name_tamil ? ` / ${item.name_tamil}` : ""}</td>
                    <td>${item.mobile || ""}</td>
                    <td>${item.aadhar_no || ""}</td>
                    <td>₹${totalChitAmount.toFixed(2)}</td>
                    <td>${item.duration || ""} (₹${splitUpAmount})</td>
                    <td>${formatDate(item.start_date)}</td>
                    <td>${formatDate(item.end_date)}</td>
                    <td>${item.status || ""}</td>
                  </tr>`;
                  })
                  .join("")}
              </tbody>
            </table>
            <div style="margin-top: 30px; text-align: center; font-size: 12px;">
              Total Records: ${filteredData.length}
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
      toast.success("Print dialog opened!", 3000, "topRight");
    } catch (error) {
      console.error("Error printing:", error);
      toast.error("Error printing. Please try again", 3000, "topRight");
    }
  };

  const exportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(18);
      doc.text("Chit List Report", 14, 22);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 35);
      const tableColumn = [
        "S.No",
        "Chit ID",
        "Customer Name",
        "Mobile Number",
        "Aadhar No",
        "Total Amount",
        "Duration",
        "Start Date",
        "End Date",
        "Status",
      ];
      const tableRows = currentItems.map((item, index) => {
        const totalChitAmount =
          parseFloat(item.amount || 0) * parseInt(item.duration || 1);
        const splitUpAmount = parseFloat(item.amount || 0).toFixed(2);
        return [
          (currentPage - 1) * itemsPerPage + index + 1,
          item.chit_id || "",
          item.name_tamil
            ? `${item.name} / ${item.name_tamil}`
            : item.name || "",
          item.mobile || "",
          item.aadhar_no || "",
          `₹${totalChitAmount.toFixed(2)}`,
          `${item.duration || ""} (₹${splitUpAmount})`,
          formatDate(item.start_date),
          formatDate(item.end_date),
          item.status || "",
        ];
      });
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 45,
        styles: { fontSize: 7 },
        headStyles: { fillColor: [66, 139, 202] },
        margin: { top: 45 },
      });
      const finalY = doc.lastAutoTable.finalY || 45;
      doc.setFontSize(10);
      doc.text(`Total Records: ${filteredData.length}`, 14, finalY + 15);
      doc.save(`chit-list-${new Date().toISOString().split("T")[0]}.pdf`);
      toast.success("PDF exported successfully!", 3000, "topRight");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Error exporting PDF. Please try again.", 3000, "topRight");
    }
  };

  const copyToClipboard = () => {
    try {
      const headers = [
        "S.No",
        "Chit ID",
        "Customer Name",
        "Mobile Number",
        "Aadhar No",
        "Total Amount",
        "Duration",
        "Start Date",
        "End Date",
        "Status",
      ];
      const rows = currentItems.map((item, index) => {
        const totalChitAmount =
          parseFloat(item.amount || 0) * parseInt(item.duration || 1);
        const splitUpAmount = parseFloat(item.amount || 0).toFixed(2);
        return [
          (currentPage - 1) * itemsPerPage + index + 1,
          item.chit_id || "",
          item.name_tamil
            ? `${item.name} / ${item.name_tamil}`
            : item.name || "",
          item.mobile || "",
          item.aadhar_no || "",
          totalChitAmount.toFixed(2),
          `${item.duration || ""} (₹${splitUpAmount})`,
          formatDate(item.start_date),
          formatDate(item.end_date),
          item.status || "",
        ];
      });
      const formattedText = [headers, ...rows]
        .map((row) => row.map((field) => `"${field}"`).join("\t"))
        .join("\n");
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(formattedText)
          .then(() =>
            toast.success("Data copied to clipboard!", 3000, "topRight"),
          )
          .catch((err) => {
            console.error("Clipboard API failed:", err);
            fallbackCopy(formattedText);
          });
      } else {
        fallbackCopy(formattedText);
      }
    } catch (error) {
      console.error("Unexpected error copying to clipboard:", error);
      toast.error("Error copying data. Please try again.", 3000, "topRight");
    }
  };

  const fallbackCopy = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.cssText =
        "position:fixed;top:0;left:0;width:2em;height:2em;padding:0;border:none;outline:none;box-shadow:none;background:transparent;";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);
      if (successful) {
        toast.success("Data copied to clipboard!", 3000, "topRight");
      } else {
        toast.error("Failed to copy data", 3000, "topRight");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      toast.error("Failed to copy data", 3000, "topRight");
    }
  };

  const exportCSV = () => {
    try {
      const headers = [
        "S.No",
        "Chit ID",
        "Customer Name",
        "Mobile Number",
        "Aadhar No",
        "Total Amount",
        "Duration",
        "Start Date",
        "End Date",
        "Status",
      ];
      const csvContent = [
        headers.join(","),
        ...currentItems.map((item, index) => {
          const totalChitAmount =
            parseFloat(item.amount || 0) * parseInt(item.duration || 1);
          const splitUpAmount = parseFloat(item.amount || 0).toFixed(2);
          return [
            (currentPage - 1) * itemsPerPage + index + 1,
            `"${item.chit_id || ""}"`,
            `"${item.name_tamil ? `${item.name} / ${item.name_tamil}` : item.name || ""}"`,
            `"${item.mobile || ""}"`,
            `"${item.aadhar_no || ""}"`,
            totalChitAmount.toFixed(2),
            `"${item.duration || ""} (₹${splitUpAmount})"`,
            `"${formatDate(item.start_date)}"`,
            `"${formatDate(item.end_date)}"`,
            `"${item.status || ""}"`,
          ].join(",");
        }),
      ].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `chit-list-${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV exported successfully!", 3000, "topRight");
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toast.error("Error exporting CSV. Please try again.", 3000, "topRight");
    }
  };

  const exportExcel = () => {
    try {
      const excelData = currentItems.map((item, index) => {
        const totalChitAmount =
          parseFloat(item.amount || 0) * parseInt(item.duration || 1);
        const splitUpAmount = parseFloat(item.amount || 0).toFixed(2);
        return {
          "S.No": (currentPage - 1) * itemsPerPage + index + 1,
          "Chit ID": item.chit_id || "",
          "Customer Name": item.name_tamil
            ? `${item.name} / ${item.name_tamil}`
            : item.name || "",
          "Mobile Number": item.mobile || "",
          "Aadhar No": item.aadhar_no || "",
          "Total Amount": totalChitAmount.toFixed(2),
          Duration: `${item.duration || ""} (₹${splitUpAmount})`,
          "Start Date": formatDate(item.start_date),
          "End Date": formatDate(item.end_date),
          Status: item.status || "",
        };
      });
      const ws = XLSX.utils.json_to_sheet(excelData);
      ws["!cols"] = [
        { wch: 8 },
        { wch: 18 },
        { wch: 25 },
        { wch: 15 },
        { wch: 15 },
        { wch: 15 },
        { wch: 20 },
        { wch: 15 },
        { wch: 15 },
        { wch: 10 },
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Chit List");
      XLSX.writeFile(
        wb,
        `chit-list-${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      toast.success("Excel file exported successfully!", 3000, "topRight");
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Error exporting Excel. Please try again.", 3000, "topRight");
    }
  };

  useEffect(() => {
    fetchData();
    fetchDataChit();
    fetchDataGenerated();
    fetchNotifications();
    fetchUserData();
    fetchLoggedInUser();
    const interval = setInterval(() => {
      fetchData();
      fetchNotifications();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchLoggedInUser = () => {
    try {
      const userInfo = JSON.parse(localStorage.getItem("user") || "{}");
      if (userInfo && userInfo.user_id) setLoggedInUser(userInfo);
    } catch (error) {
      console.error("Error reading logged-in user:", error);
    }
  };

  const fetchUserData = async () => {
    try {
      const res = await axios.get(`${ApiUrl}/Api/user.php`);
      const users = Array.isArray(res.data.data) ? res.data.data : [];
      setUserdata(users);
    } catch (err) {
      console.error("Failed to fetch users", err);
      toast.error("Failed to load staff data", 3000, "topRight");
    }
  };

  useEffect(() => {
    if (userdata.length > 0) {
      const allStaff = userdata.filter(
        (u) => u.role === "Employee" || u.role === "employee",
      );
      setStaffList(allStaff);
    }
  }, [userdata]);

  useEffect(() => {
    if (!loggedInUser || userdata.length === 0) return;
    const currentUser = userdata.find(
      (u) => u.user_id === loggedInUser.user_id,
    );
    if (!currentUser) return;
    if (currentUser.role === "Manager" || currentUser.role === "manager") {
      setSelectedManager(currentUser.id.toString());
      setIsManagerLocked(true);
      const staff = userdata.filter(
        (u) =>
          u.manager_id === currentUser.user_id &&
          (u.role === "Employee" || u.role === "employee"),
      );
      setStaffList(staff);
      setAddData((prev) => ({
        ...prev,
        manager: currentUser.user_id,
        manager_name: currentUser.name,
      }));
    } else if (
      currentUser.role === "Employee" ||
      currentUser.role === "employee"
    ) {
      setSelectedStaff(currentUser.name);
      setIsStaffLocked(true);
      const manager = userdata.find(
        (u) =>
          u.user_id === currentUser.manager_id &&
          (u.role === "Manager" || u.role === "manager"),
      );
      if (manager) {
        setSelectedManager(manager.id.toString());
        setIsManagerLocked(true);
        const staffUnderManager = userdata.filter(
          (u) =>
            u.manager_id === manager.user_id &&
            (u.role === "Employee" || u.role === "employee"),
        );
        setStaffList(staffUnderManager);
        setAddData((prev) => ({
          ...prev,
          manager: manager.user_id,
          manager_name: manager.name,
          staff: currentUser.name,
        }));
      }
    } else {
      const allStaff = userdata.filter(
        (u) => u.role === "Employee" || u.role === "employee",
      );
      setStaffList(allStaff);
    }
  }, [loggedInUser, userdata]);

  const handleManagerChange = (e) => {
    if (isManagerLocked) return;
    const managerId = e.target.value;
    setSelectedManager(managerId);
    if (managerId) {
      const managerObj = userdata.find((u) => u.id === parseInt(managerId));
      if (managerObj) {
        const allStaff = userdata.filter(
          (u) => u.role === "Employee" || u.role === "employee",
        );
        setStaffList(allStaff);
        setAddData((prev) => ({
          ...prev,
          manager: managerObj.user_id,
          manager_name: managerObj.name,
        }));
        setUpdateData((prev) => ({
          ...prev,
          manager: managerObj.user_id,
          manager_name: managerObj.name,
        }));
      }
    } else {
      const allStaff = userdata.filter(
        (u) => u.role === "Employee" || u.role === "employee",
      );
      setStaffList(allStaff);
      setAddData((prev) => ({ ...prev, manager: "", manager_name: "" }));
      setUpdateData((prev) => ({ ...prev, manager: "", manager_name: "" }));
    }
  };

  const handleStaffChange = (e) => {
    if (isStaffLocked) return;
    const staffName = e.target.value;
    setSelectedStaff(staffName);
    setAddData((prev) => ({ ...prev, staff: staffName }));
    setUpdateData((prev) => ({ ...prev, staff: staffName }));
  };

  // Open Close Chit Modal
  const handleOpenCloseChit = async (chit) => {
    const payments = chit.payment_history || [];
    const totalPaid = payments.reduce(
      (sum, p) => sum + parseFloat(p.paid || 0),
      0,
    );
    const totalCashPaid = payments
      .filter((p) => (p.payment_type || "").toLowerCase() === "cash")
      .reduce((sum, p) => sum + parseFloat(p.paid || 0), 0);
    const totalGoldPaid = payments
      .filter((p) => (p.payment_type || "").toLowerCase() === "gold")
      .reduce((sum, p) => sum + parseFloat(p.paid || 0), 0);
    const totalAmount =
      parseFloat(chit.amount || 0) * parseInt(chit.duration || 1);
    const remainingBalance = totalAmount - totalPaid;

    let goldRate = todayGoldRate;
    let pureGoldRate = todayPureGoldRate;
    let silverRate = todaySilverRate;
    if (goldRate <= 0 || pureGoldRate <= 0 || silverRate <= 0) {
      try {
        const rateRes = await axios.get(
          `${ApiUrl}/Api/daily_rates.php/current`,
        );
        const rateData = rateRes.data?.data;
        const todayRate = Array.isArray(rateData)
          ? rateData[0] || {}
          : rateData || {};
        goldRate = parseFloat(todayRate?.gold_1g_rate || 0);
        pureGoldRate = parseFloat(
          todayRate?.gold_pure_percentage || todayRate?.gold_pure_rate || 0,
        );
        silverRate = parseFloat(
          todayRate?.silver_10g_rate || todayRate?.silver_pure_percentage || 0,
        );
        if (goldRate > 0) setTodayGoldRate(goldRate);
        if (pureGoldRate > 0) setTodayPureGoldRate(pureGoldRate);
        if (silverRate > 0) setTodaySilverRate(silverRate);
      } catch {}
    }

    setCloseChitData({
      ...chit,
      totalPaid,
      remainingBalance,
      totalCashPaid,
      totalGoldPaid,
      totalAmount,
      goldRate,
      pureGoldRate,
      silverRate,
    });

    const returnAmount = totalPaid;
    if (pureGoldRate > 0) {
      setCloseGoldWeight((returnAmount / pureGoldRate).toFixed(3));
    } else if (goldRate > 0) {
      setCloseGoldWeight((returnAmount / goldRate).toFixed(3));
    } else {
      setCloseGoldWeight("");
    }
    if (silverRate > 0) {
      setCloseSilverWeight((returnAmount / silverRate).toFixed(3));
    } else {
      setCloseSilverWeight("");
    }
    setCloseReturnType("cash");
    setCloseChitModal(true);
  };

  // Auto-assign code + input number for Add Chit (read-only, no manual picking)
  useEffect(() => {
    if (showModal) {
      const activeCode = getActiveCode();
      if (activeCode) {
        const nextNumber = getNextAvailableInputNumber(activeCode);
        setAddData((prev) => ({
          ...prev,
          code: activeCode,
          input_number: nextNumber.toString(),
        }));
      }
    }
  }, [showModal, usedInputNumbers]);

  // Confirm Close Chit
  const handleCloseChitConfirm = async () => {
    if (!closeChitData) return;
    try {
      setCloseLoading(true);
      const returnAmount = closeChitData.totalPaid;
      const payload = {
        return_type: closeReturnType,
        return_amount: returnAmount.toFixed(2),
        gold_weight: closeReturnType === "gold" ? closeGoldWeight : "",
        silver_weight: closeReturnType === "silver" ? closeSilverWeight : "",
        paid_amount: closeChitData.totalPaid.toFixed(2),
        remaining_balance: closeChitData.remainingBalance.toFixed(2),
        updated_by: localStorage.getItem("username") || "",
        updated_date: new Date().toISOString().slice(0, 19).replace("T", " "),
      };
      await axios.put(
        `${ApiUrl}/Api/chit_list.php/${closeChitData.id}`,
        payload,
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success(
        `Chit closed successfully! Returned ₹${returnAmount.toFixed(2)} to customer.`,
        4000,
        "topRight",
      );
      setCloseChitModal(false);
      fetchData();
    } catch (error) {
      console.error("Close chit error:", error);
      toast.error(
        "Failed to close chit: " +
          (error.response?.data?.message || error.message),
        3000,
        "topRight",
      );
    } finally {
      setCloseLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <div className="loading-message">
            <div className="loadinggif"></div>
          </div>
        </div>
      )}
      <div className="container-fluid py-3">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex justify-content-between mb-4">
            <div className="d-flex gap-3">
              <h6 className="card-title m-2">Chit List</h6>
              <button
                className="btn btn-primary btn-sm rounded-3"
                onClick={() => setShowModal(true)}
              >
                Add Chit <span className="ms-2">+</span>
              </button>
              <button
                className="btn btn-success btn-sm mx-2 rounded-3"
                onClick={() => setPaymentModal(true)}
              >
                Add Payment <span className="ms-2">+</span>
              </button>
            </div>
          </div>
        </div>
        <div className="card shadow-sm">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex">
                <label className="form-label text-dark mt-2">Show : </label>
                <div>
                  <select
                    className="form-control mx-2"
                    onChange={handleItemsPerPageChange}
                    value={showAllItems ? "All" : itemsPerPage}
                  >
                    <option value="All">All</option>
                    {[5, 10, 20, 100, 200, 500, 1000].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={handlePrint}
                >
                  Print
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={exportPDF}
                >
                  PDF
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={copyToClipboard}
                >
                  Copy
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={exportCSV}
                >
                  CSV
                </button>
                <button
                  className="btn btn-sm btn-info text-white mx-1"
                  onClick={exportExcel}
                >
                  Excel
                </button>
              </div>
              <div className="search-container">
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="card tableCard p-2">
              <div className="table-container">
                <table className="table table-layout-fixed w-100">
                  <thead className="bg-light">
                    <tr>
                      <th style={{ width: "10%" }}>S.No</th>
                      <th>Chit ID</th>
                      <th>Customer Name</th>
                      <th>Mobile Number</th>
                      <th>Aadhar No</th>
                      <th>Total Chit Amount</th>
                      <th>Duration (Months)</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item, index) => {
                      const notification = notifications.find(
                        (notif) =>
                          notif.table_id === item.id.toString() &&
                          notif.viewed === "false",
                      );
                      let rowClass = "";
                      if (notification) {
                        switch (notification.status) {
                          case "accepted":
                            rowClass = "table-secondary";
                            break;
                          case "rejected":
                            rowClass = "table-danger";
                            break;
                          case "requested":
                            rowClass = "table-info";
                            break;
                        }
                      }
                      const totalChitAmount =
                        parseFloat(item.amount || 0) *
                        parseInt(item.duration || 1);
                      const splitUpAmount = parseFloat(
                        item.amount || 0,
                      ).toFixed(2);
                      return (
                        <tr key={index} className={rowClass}>
                          <td>
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </td>
                          <td>{item.chit_id}</td>
                          <td>
                            {item.name}
                            {item.name_tamil && (
                              <span className="text-muted">
                                {" "}
                                / {item.name_tamil}
                              </span>
                            )}
                          </td>
                          <td>{item.mobile}</td>
                          <td>{item.aadhar_no || "-"}</td>
                          <td>₹{totalChitAmount.toFixed(2)}</td>
                          <td>
                            {item.duration || 0}
                            <small className="text-muted d-block">
                              (₹{splitUpAmount} × {item.duration || 0})
                            </small>
                          </td>
                          <td>
                            {item.status === "Open" ? (
                              <span className="badge bg-success text-white">
                                Open
                              </span>
                            ) : item.status === "Close" ? (
                              <span className="badge bg-danger text-white">
                                Close
                              </span>
                            ) : (
                              <span className="badge bg-secondary text-white">
                                {item.status}
                              </span>
                            )}
                          </td>
                          <td className="text-start">
                            <button
                              className="btn btn-sm btn-light me-1"
                              onClick={() => openModelHistory(index)}
                            >
                              <i className="fa-solid fa-plus text-success fs-6"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-light me-1"
                              onClick={() => handleUpdate(item.id, item)}
                            >
                              <i className="fa-solid fa-pen-to-square text-primary fs-6"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-light me-1"
                              onClick={() => handleprint(item.id)}
                            >
                              <i className="fa-solid fa-print text-secondary fs-6"></i>
                            </button>
                            {item.status !== "Close" && (
                              <button
                                className="btn btn-sm btn-light me-1"
                                title="Close Chit"
                                onClick={() => handleOpenCloseChit(item)}
                              >
                                <i className="fa-solid fa-circle-xmark text-warning fs-6"></i>
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-light me-1"
                              onClick={() =>
                                handleOpenModal(item.id, item.name)
                              }
                            >
                              <i className="fa-solid fa-trash text-danger fs-6"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-light"
                              onClick={() =>
                                navigate(
                                  `/chit/chit-list/payment-history/${item.customer_id}`,
                                )
                              }
                            >
                              <i className="fa-solid fa-info text-secondary fs-6"></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="row">
                <div className="col-lg-6">
                  <p className="text-muted">
                    Showing{" "}
                    {showAllItems ? 1 : (currentPage - 1) * itemsPerPage + 1} to{" "}
                    {showAllItems
                      ? totalItems
                      : Math.min(currentPage * itemsPerPage, totalItems)}{" "}
                    of {totalItems} entries
                  </p>
                </div>
                <div className="col-lg-6">
                  <nav aria-label="Page navigation">
                    <ul className="pagination justify-content-end">
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(1)}
                          aria-label="First"
                        >
                          <span aria-hidden="true">««</span>
                        </button>
                      </li>
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          aria-label="Previous"
                        >
                          <span aria-hidden="true">«</span>
                        </button>
                      </li>
                      {renderPageNumbers()}
                      <li
                        className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          aria-label="Next"
                        >
                          <span aria-hidden="true">»</span>
                        </button>
                      </li>
                      <li
                        className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(totalPages)}
                          aria-label="Last"
                        >
                          <span aria-hidden="true">»»</span>
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Modal */}
      <div
        className={`modal fade ${showNotificationModal ? "show" : ""}`}
        style={{
          display: showNotificationModal ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title text-primary">
                Send {actionType} Request to Admin
              </h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeNotificationModal}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleNotificationSubmit}>
                <p className="text-center">
                  Do you want to request to {actionType.toLowerCase()} this
                  chit?
                </p>
                <div className="text-center mt-4">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm px-4 me-2"
                    onClick={closeNotificationModal}
                  >
                    No
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm px-4"
                    disabled={isSubmitting}
                  >
                    Yes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Add Chit Modal */}
      <div
        className={`modal fade ${showModal ? "show" : ""}`}
        style={{ display: showModal ? "block" : "none" }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title text-primary">Add Chit</h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeModal}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Chit ID */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        value={
                          addData.code && addData.input_number && addData.amount
                            ? generateChitId(
                                addData.code,
                                addData.input_number,
                                addData.amount,
                              )
                            : "Will be generated automatically"
                        }
                        readOnly
                        disabled
                        style={{ backgroundColor: "#f8f9fa", color: "#6c757d" }}
                      />
                      <label>Chit ID</label>
                    </div>
                    {addData.code && addData.input_number && addData.amount && (
                      <small
                        className={
                          isChitIdExists(
                            addData.code,
                            addData.input_number,
                            addData.amount,
                          )
                            ? "text-danger"
                            : "text-success"
                        }
                      >
                        {isChitIdExists(
                          addData.code,
                          addData.input_number,
                          addData.amount,
                        )
                          ? "⚠️ This Chit ID already exists!"
                          : "✓ Available"}
                      </small>
                    )}
                  </div>

                  {/* Manager */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <select
                        className="form-select"
                        value={selectedManager}
                        onChange={handleManagerChange}
                        disabled={isManagerLocked}
                      >
                        <option value="">Select Manager</option>
                        {userdata
                          .filter(
                            (u) => u.role === "Manager" || u.role === "manager",
                          )
                          .map((manager) => (
                            <option key={manager.id} value={manager.id}>
                              {manager.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  {/* Staff */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <select
                        className="form-select"
                        value={selectedStaff}
                        onChange={handleStaffChange}
                        disabled={isStaffLocked}
                      >
                        <option value="">Select Staff</option>
                        {staffList.map((staff) => (
                          <option key={staff.id} value={staff.name}>
                            {staff.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Customer ID */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        value={generate}
                        required
                        disabled
                      />
                      <label>Customer ID</label>
                    </div>
                  </div>

                  {/* Customer Name with Tamil Translation */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="add_name"
                          placeholder="Customer Name"
                          value={addData.name}
                          onChange={(e) =>
                            setAddData({
                              ...addData,
                              name: e.target.value,
                              name_tamil: "",
                            })
                          }
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if (val && !addData.name_tamil) {
                              const tamil = await translateToTamil(val);
                              if (tamil)
                                setAddData((prev) => ({
                                  ...prev,
                                  name_tamil: tamil,
                                }));
                            }
                          }}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={isTranslating || !addData.name}
                          onClick={async () => {
                            const tamil = await translateToTamil(addData.name);
                            if (tamil) {
                              setAddData((prev) => ({
                                ...prev,
                                name_tamil: tamil,
                              }));
                              toast.success("Translated!", 1500, "topRight");
                            }
                          }}
                        >
                          {isTranslating ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            "🌐 தமிழ்"
                          )}
                        </button>
                      </div>
                      {addData.name_tamil && (
                        <small className="text-muted mt-1 d-block">
                          <strong>Tamil:</strong> {addData.name_tamil}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 text-muted"
                            onClick={() =>
                              setAddData((prev) => ({
                                ...prev,
                                name_tamil: "",
                              }))
                            }
                          >
                            ✕ clear
                          </button>
                        </small>
                      )}
                      <label htmlFor="add_name">Enter Customer Name</label>
                    </div>
                  </div>

                  {/* Mobile */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="Mobile Number"
                        value={addData.mobile}
                        maxLength={10}
                        minLength={10}
                        pattern="\d{10}"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,10}$/.test(value)) {
                            setAddData({ ...addData, mobile: value });
                          }
                        }}
                        required
                      />
                      <label>Enter Mobile Number</label>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Address"
                        value={addData.address}
                        onChange={(e) =>
                          setAddData({ ...addData, address: e.target.value })
                        }
                        required
                      />
                      <label>Enter Address</label>
                    </div>
                  </div>

                  {/* Aadhar Number - Mandatory */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="Aadhar No"
                        value={addData.aadhar_no}
                        maxLength={12}
                        minLength={12}
                        pattern="\d{12}"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,12}$/.test(value)) {
                            setAddData({ ...addData, aadhar_no: value });
                          }
                        }}
                        required
                      />
                      <label>
                        Aadhar Number <span className="text-danger">*</span>
                      </label>
                    </div>
                  </div>

                  {/* Code - custom dropdown so a fully-used letter can still be shown,
                      disabled, and tapping/clicking it shows a tooltip/toast instead of
                      silently doing nothing (native <select disabled> options don't fire
                      click events, so touch users would get no feedback at all). */}
               {/* Code - auto-assigned, read-only */}
<div className="col-lg-6">
  <div className="floating-label">
    <input
      type="text"
      className="form-control"
      value={addData.code || "Calculating..."}
      readOnly
      disabled
      style={{ backgroundColor: "#f8f9fa", color: "#6c757d" }}
    />
    <label>Code</label>
  </div>
  {/* {addData.code && (
    <small className="text-muted d-block mt-1">
      Used: {usedInputNumbers[addData.code]?.length || 0}/500
    </small>
  )} */}
</div>

                  {/* Input Number */}
                {/* Input Number - auto-assigned, read-only */}
<div className="col-lg-6">
  <div className="floating-label">
    <input
      type="text"
      className="form-control"
      value={addData.input_number || ""}
      readOnly
      disabled
      style={{ backgroundColor: "#f8f9fa", color: "#6c757d" }}
    />
    <label>Input Number</label>
  </div>
</div>

                  {/* Amount */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Amount"
                        value={addData.amount}
                        onChange={(e) =>
                          setAddData({ ...addData, amount: e.target.value })
                        }
                        required
                      />
                      <label>Monthly Amount (₹)</label>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Duration"
                        value={addData.duration}
                        onChange={(e) =>
                          setAddData({ ...addData, duration: e.target.value })
                        }
                        required
                      />
                      <label>Duration (Months)</label>
                    </div>
                  </div>

                  {/* Start Date */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="date"
                        className="form-control"
                        value={addData.start_date}
                        onChange={(e) =>
                          setAddData({ ...addData, start_date: e.target.value })
                        }
                        required
                      />
                      <label>Start Date</label>
                    </div>
                  </div>

                  {/* End Date */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="date"
                        className="form-control"
                        value={addData.end_date}
                        onChange={(e) =>
                          setAddData({ ...addData, end_date: e.target.value })
                        }
                      />
                      <label>End Date</label>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <select
                        className="form-select"
                        value={addData.status}
                        onChange={(e) =>
                          setAddData({ ...addData, status: e.target.value })
                        }
                        required
                      >
                        <option value="">Select Status</option>
                        <option value="Open">Open</option>
                        <option value="Close">Close</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="text-center mt-2">
                  <button type="submit" className="btn btn-primary btn-sm px-4">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Details Modal - Removed Balance Column */}
      <div
        className={`modal fade ${infoModal ? "show" : ""}`}
        style={{ display: infoModal ? "block" : "none" }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title text-primary">Payment Details</h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closePaymentTable}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={AddPayment}>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Chit ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={payment.chit_id}
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Customer ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={payment.customer_id}
                        readOnly
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Total Amount</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          selectedChit
                            ? `₹${parseFloat(selectedChit.amount || 0).toFixed(2)}`
                            : payment.total
                        }
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Overall Paid</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          selectedChit
                            ? `₹${calculateOverallAmounts(selectedChit).totalPaid.toFixed(2)}`
                            : payment.paid
                        }
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Overall Balance</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          selectedChit
                            ? `₹${calculateOverallAmounts(selectedChit).totalBalance.toFixed(2)}`
                            : payment.balance
                        }
                        readOnly
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Payment Type</label>
                      <select
                        className="form-select"
                        value={payment.payment_type}
                        onChange={(e) =>
                          setPayment({
                            ...payment,
                            payment_type: e.target.value,
                          })
                        }
                      >
                        <option value="">Select Payment Type</option>
                        <option value="Cash">Cash</option>
                        <option value="Gpay">Gpay</option>
                        <option value="Phonepe">Phonepe</option>
                        <option value="Paytm">Paytm</option>
                        <option value="Credit">Credit</option>
                        <option value="Debit">Debit</option>
                      </select>
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Amount</label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={payment.amount}
                        onChange={(e) =>
                          setPayment({ ...payment, amount: e.target.value })
                        }
                        placeholder="Enter Amount"
                      />
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Payment Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={payment.payment_date}
                        onChange={(e) =>
                          setPayment({
                            ...payment,
                            payment_date: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>
                <div
                  className="card mt-3"
                  style={{ borderRadius: "5px", backgroundColor: "#E8F0FA" }}
                >
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Payment Type</th>
                            <th>Paid Amount</th>
                            <th>Payment Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedChit?.payment_history?.length > 0 ? (
                            selectedChit.payment_history.map(
                              (payment, index) => (
                                <tr key={index}>
                                  <td>{index + 1}</td>
                                  <td>{payment.payment_type}</td>
                                  <td>
                                    ₹{parseFloat(payment.paid || 0).toFixed(2)}
                                  </td>
                                  <td>{formatDate(payment.payment_date)}</td>
                                </tr>
                              ),
                            )
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                No payments added
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <div className="d-flex justify-content-center gap-2 mt-2">
                  <button
                    type="submit"
                    className="btn btn-success btn-sm"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                  <button
                    onClick={() => setInfoModal(false)}
                    className="btn btn-dark btn-sm text-white"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Update Chit Modal */}
      <div
        className={`modal fade ${updateModal ? "show" : ""}`}
        style={{ display: updateModal ? "block" : "none" }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title text-primary">Update Chit</h6>
              <button
                type="button"
                className="btn-close"
                style={{ fontSize: "10px" }}
                onClick={closeUpdateModal}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleUpdateSubmit}>
                <div className="row">
                  {/* Chit ID */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        value={
                          updateData.code &&
                          updateData.input_number &&
                          updateData.amount
                            ? generateChitId(
                                updateData.code,
                                updateData.input_number,
                                updateData.amount,
                              )
                            : updateData.chit_id ||
                              "Will be generated automatically"
                        }
                        readOnly
                        disabled
                        style={{ backgroundColor: "#f8f9fa", color: "#6c757d" }}
                      />
                      <label>Chit ID</label>
                    </div>
                    {updateData.code &&
                      updateData.input_number &&
                      updateData.amount && (
                        <small
                          className={
                            isChitIdExists(
                              updateData.code,
                              updateData.input_number,
                              updateData.amount,
                            )
                              ? "text-danger"
                              : "text-success"
                          }
                        >
                          {isChitIdExists(
                            updateData.code,
                            updateData.input_number,
                            updateData.amount,
                          )
                            ? "⚠️ This Chit ID already exists!"
                            : "✓ Available"}
                        </small>
                      )}
                  </div>

                  {/* Customer ID */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        value={updateData.customer_id}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            customer_id: e.target.value,
                          })
                        }
                        required
                        readOnly
                        disabled
                      />
                      <label>Customer ID</label>
                    </div>
                  </div>

                  {/* Manager */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <select
                        className="form-select"
                        value={selectedManager}
                        onChange={handleManagerChange}
                        disabled={isManagerLocked}
                      >
                        <option value="">Select Manager</option>
                        {userdata
                          .filter(
                            (u) => u.role === "Manager" || u.role === "manager",
                          )
                          .map((manager) => (
                            <option key={manager.id} value={manager.id}>
                              {manager.name}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  {/* Staff */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <select
                        className="form-select"
                        value={selectedStaff}
                        onChange={handleStaffChange}
                        disabled={isStaffLocked}
                      >
                        <option value="">Select Staff</option>
                        {staffList.map((staff) => (
                          <option key={staff.id} value={staff.name}>
                            {staff.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Customer Name with Tamil Translation */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          id="update_name"
                          placeholder="Customer Name"
                          value={updateData.name}
                          onChange={(e) =>
                            setUpdateData({
                              ...updateData,
                              name: e.target.value,
                              name_tamil: "",
                            })
                          }
                          onBlur={async (e) => {
                            const val = e.target.value.trim();
                            if (val) {
                              const tamil = await translateToTamil(val);
                              if (tamil)
                                setUpdateData((prev) => ({
                                  ...prev,
                                  name_tamil: tamil,
                                }));
                            }
                          }}
                          required
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          disabled={isTranslating || !updateData.name}
                          onClick={async () => {
                            const tamil = await translateToTamil(
                              updateData.name,
                            );
                            if (tamil) {
                              setUpdateData((prev) => ({
                                ...prev,
                                name_tamil: tamil,
                              }));
                              toast.success("Translated!", 1500, "topRight");
                            }
                          }}
                        >
                          {isTranslating ? (
                            <span className="spinner-border spinner-border-sm" />
                          ) : (
                            "🌐 தமிழ்"
                          )}
                        </button>
                      </div>
                      {updateData.name_tamil && (
                        <small className="text-muted mt-1 d-block">
                          <strong>Tamil:</strong> {updateData.name_tamil}
                          <button
                            type="button"
                            className="btn btn-link btn-sm p-0 ms-2 text-muted"
                            onClick={() =>
                              setUpdateData((prev) => ({
                                ...prev,
                                name_tamil: "",
                              }))
                            }
                          >
                            ✕ clear
                          </button>
                        </small>
                      )}
                      <label htmlFor="update_name">Customer Name</label>
                    </div>
                  </div>

                  {/* Mobile */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d{10}"
                        maxLength={10}
                        minLength={10}
                        className="form-control"
                        placeholder="Mobile Number"
                        value={updateData.mobile}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,10}$/.test(value)) {
                            setUpdateData({ ...updateData, mobile: value });
                          }
                        }}
                        required
                      />
                      <label>Mobile Number</label>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Address"
                        value={updateData.address}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            address: e.target.value,
                          })
                        }
                        required
                      />
                      <label>Address</label>
                    </div>
                  </div>

                  {/* Aadhar Number - Mandatory */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        placeholder="Aadhar No"
                        value={updateData.aadhar_no || ""}
                        maxLength={12}
                        minLength={12}
                        pattern="\d{12}"
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,12}$/.test(value)) {
                            setUpdateData({ ...updateData, aadhar_no: value });
                          }
                        }}
                        required
                      />
                      <label>
                        Aadhar Number <span className="text-danger">*</span>
                      </label>
                    </div>
                  </div>

                  {/* Code - same custom dropdown behavior (disabled + tooltip/toast when full) */}
               <div className="col-lg-6">
  <div className="floating-label">
    <input
      type="text"
      className="form-control"
      value={updateData.code || ""}
      readOnly
      disabled
      style={{ backgroundColor: "#f8f9fa", color: "#6c757d" }}
    />
    <label>Code</label>
  </div>
</div>

                  {/* Input Number */}
                 <div className="col-lg-6">
  <div className="floating-label">
    <input
      type="text"
      className="form-control"
      value={updateData.input_number || ""}
      readOnly
      disabled
      style={{ backgroundColor: "#f8f9fa", color: "#6c757d" }}
    />
    <label>Input Number</label>
  </div>
</div>

                  {/* Amount */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Amount"
                        value={updateData.amount}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            amount: e.target.value,
                          })
                        }
                        required
                      />
                      <label>Monthly Amount (₹)</label>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Duration"
                        value={updateData.duration}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            duration: e.target.value,
                          })
                        }
                        required
                      />
                      <label>Duration (Months)</label>
                    </div>
                  </div>

                  {/* Start Date */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="date"
                        className="form-control"
                        value={updateData.start_date}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            start_date: e.target.value,
                          })
                        }
                        required
                      />
                      <label>Start Date</label>
                    </div>
                  </div>

                  {/* End Date */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <input
                        type="date"
                        className="form-control"
                        value={updateData.end_date}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            end_date: e.target.value,
                          })
                        }
                      />
                      <label>End Date</label>
                    </div>
                  </div>

                  {/* Status */}
                  <div className="col-lg-6">
                    <div className="floating-label">
                      <select
                        className="form-select"
                        value={updateData.status}
                        onChange={(e) =>
                          setUpdateData({
                            ...updateData,
                            status: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select Status</option>
                        <option value="Open">Open</option>
                        <option value="Close">Close</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="text-center mt-4">
                  <button type="submit" className="btn btn-primary btn-sm px-4">
                    Update
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <div
        className={`modal fade ${deleteShowModals ? "show" : ""}`}
        style={{
          display: deleteShowModals ? "block" : "none",
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
        tabIndex="-1"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-confirm">
          <div className="modal-content">
            <div className="modal-body text-center">
              <div className="d-flex justify-content-center mb-3">
                <div
                  className="icon-box d-flex justify-content-center align-items-center bg-light rounded-circle"
                  style={{ width: "80px", height: "80px", margin: "0 auto" }}
                >
                  <i className="bi bi-exclamation-triangle text-danger fs-2"></i>
                </div>
              </div>
              <h4 className="modal-title mb-3 text-center">Are you sure?</h4>
              <h6 className="mb-4 text-center">
                Do you really want to delete this chit for
                <span className="text-primary"> {getDeleteName}</span>?
              </h6>
              <div className="d-flex btn-group">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm flex-grow-1"
                  onClick={handleCloseModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm flex-grow-1 mx-2"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Payment Modal */}
      <div
        className={`modal fade ${paymentModal ? "show" : ""}`}
        style={{ display: paymentModal ? "block" : "none" }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form onSubmit={Addpaymentmodal}>
              <div className="modal-header">
                <h6 className="modal-title text-primary">Add Payment</h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closePayment}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Chit ID</label>
                      <select
                        className="form-select"
                        value={payment.chit_id}
                        onChange={handleChitChange}
                        required
                      >
                        <option value="">-- Select Chit --</option>
                        {responseData.map((item) => (
                          <option key={item.id} value={item.chit_id}>
                            {item.chit_id} - {item.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Customer ID</label>
                      <input
                        type="text"
                        className="form-control"
                        value={payment.customer_id}
                        placeholder="Auto-filled"
                        readOnly
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Total</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          selectedChit
                            ? `₹${parseFloat(selectedChit.amount || 0).toFixed(2)}`
                            : ""
                        }
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Paid</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          selectedChit
                            ? `₹${calculateOverallAmounts(selectedChit).totalPaid.toFixed(2)}`
                            : ""
                        }
                        readOnly
                      />
                    </div>
                  </div>
                  <div className="col-lg-4">
                    <div className="mb-3">
                      <label className="form-label">Balance</label>
                      <input
                        type="text"
                        className="form-control"
                        value={
                          selectedChit
                            ? `₹${calculateOverallAmounts(selectedChit).totalBalance.toFixed(2)}`
                            : ""
                        }
                        readOnly
                      />
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Payment Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={payment.payment_date}
                        onChange={(e) =>
                          setPayment({
                            ...payment,
                            payment_date: e.target.value,
                          })
                        }
                        required
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Payment Type</label>
                      <select
                        className="form-select"
                        value={payment.payment_type}
                        onChange={(e) =>
                          setPayment({
                            ...payment,
                            payment_type: e.target.value,
                          })
                        }
                        required
                      >
                        <option value="">Select Payment Type</option>
                        <option value="Cash">Cash</option>
                        <option value="Gpay">Gpay</option>
                        <option value="Phonepe">Phonepe</option>
                        <option value="Paytm">Paytm</option>
                        <option value="Credit">Credit</option>
                        <option value="Debit">Debit</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={payment.amount}
                    onChange={(e) =>
                      setPayment({ ...payment, amount: e.target.value })
                    }
                    placeholder="Enter Amount"
                    required
                  />
                </div>
                <div className="text-end mt-2">
                  <button
                    type="submit"
                    className="btn btn-success btn-sm"
                    disabled={loading}
                  >
                    {loading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Close Chit Modal */}
      {closeChitModal && closeChitData && (
        <div
          className="modal fade show"
          style={{
            display: "block",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1060,
          }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title text-warning">
                  <i className="fa-solid fa-circle-xmark me-2"></i>Close Chit —{" "}
                  {closeChitData.chit_id}
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={() => setCloseChitModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="row mb-2">
                  <div className="col-6">
                    <strong>Customer:</strong>
                  </div>
                  <div className="col-6">
                    {closeChitData.name}
                    {closeChitData.name_tamil && (
                      <span className="text-muted">
                        {" "}
                        / {closeChitData.name_tamil}
                      </span>
                    )}
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-6">
                    <strong>Monthly Amount:</strong>
                  </div>
                  <div className="col-6">
                    ₹{parseFloat(closeChitData.amount || 0).toFixed(2)}
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-6">
                    <strong>Duration:</strong>
                  </div>
                  <div className="col-6">{closeChitData.duration} months</div>
                </div>
                <div className="row mb-2">
                  <div className="col-6">
                    <strong>Total Expected:</strong>
                  </div>
                  <div className="col-6">
                    ₹{closeChitData.totalAmount.toFixed(2)}
                  </div>
                </div>
                <div className="row mb-2">
                  <div className="col-6">
                    <strong>Total Paid:</strong>
                  </div>
                  <div className="col-6 fw-bold text-success">
                    ₹{closeChitData.totalPaid.toFixed(2)}
                  </div>
                </div>

                {closeChitData.remainingBalance > 0 && (
                  <div className="alert alert-warning py-2 small">
                    <i className="fa-solid fa-circle-exclamation me-2"></i>
                    Remaining balance:{" "}
                    <strong>
                      ₹{closeChitData.remainingBalance.toFixed(2)}
                    </strong>
                    <br />
                    <small>
                      Customer will forfeit the remaining balance upon closing.
                    </small>
                  </div>
                )}

                {closeChitData.excess > 0 && (
                  <div className="alert alert-success py-2 small">
                    <i className="fa-solid fa-circle-info me-2"></i>
                    Extra paid:{" "}
                    <strong>₹{closeChitData.excess.toFixed(2)}</strong>
                  </div>
                )}

                <div className="alert alert-info py-2 small mb-3">
                  Return <strong>₹{closeChitData.totalPaid.toFixed(2)}</strong>{" "}
                  as:
                </div>

                <div className="mb-3">
                  <div className="d-flex gap-3 flex-wrap">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="returnCash"
                        value="cash"
                        checked={closeReturnType === "cash"}
                        onChange={() => {
                          setCloseReturnType("cash");
                          setCloseGoldWeight("");
                          setCloseSilverWeight("");
                        }}
                      />
                      <label className="form-check-label" htmlFor="returnCash">
                        <i className="fa-solid fa-money-bill me-1 text-success"></i>
                        Cash
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="returnGold"
                        value="gold"
                        checked={closeReturnType === "gold"}
                        onChange={() => {
                          setCloseReturnType("gold");
                          setCloseSilverWeight("");
                          const pureRate =
                            closeChitData.pureGoldRate || todayPureGoldRate;
                          const rate1g =
                            closeChitData.goldRate || todayGoldRate;
                          const useRate = pureRate > 0 ? pureRate : rate1g;
                          setCloseGoldWeight(
                            useRate > 0
                              ? (closeChitData.totalPaid / useRate).toFixed(3)
                              : "",
                          );
                        }}
                      />
                      <label className="form-check-label" htmlFor="returnGold">
                        <i className="fa-solid fa-coins me-1 text-warning"></i>
                        Gold (Pure)
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="returnSilver"
                        value="silver"
                        checked={closeReturnType === "silver"}
                        onChange={() => {
                          setCloseReturnType("silver");
                          setCloseGoldWeight("");
                          const sRate =
                            closeChitData.silverRate || todaySilverRate;
                          setCloseSilverWeight(
                            sRate > 0
                              ? (closeChitData.totalPaid / sRate).toFixed(3)
                              : "",
                          );
                        }}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="returnSilver"
                      >
                        <i className="fa-solid fa-circle me-1 text-secondary"></i>
                        Silver
                      </label>
                    </div>
                  </div>
                </div>

                {closeReturnType === "cash" && (
                  <div className="alert alert-light py-2 small">
                    ₹{closeChitData.totalPaid.toFixed(2)} will be returned as
                    cash to customer.
                  </div>
                )}

                {closeReturnType === "gold" &&
                  (() => {
                    const pureRate =
                      closeChitData.pureGoldRate || todayPureGoldRate;
                    const rate1g = closeChitData.goldRate || todayGoldRate;
                    const useRate = pureRate > 0 ? pureRate : rate1g;
                    const autoGrams =
                      useRate > 0
                        ? (closeChitData.totalPaid / useRate).toFixed(3)
                        : "0.000";
                    return (
                      <div className="mb-2">
                        <label className="form-label">
                          Gold Weight to Return (g)
                          {useRate > 0 && (
                            <span className="text-muted ms-2 small">
                              @ ₹{useRate.toFixed(2)}/g (Pure Gold Rate)
                            </span>
                          )}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Gold weight in grams"
                          value={closeGoldWeight}
                          onChange={(e) => setCloseGoldWeight(e.target.value)}
                        />
                        {useRate > 0 && (
                          <small className="text-muted">
                            Pure gold rate: ₹{useRate.toFixed(2)}/g —{" "}
                            {closeChitData.totalPaid.toFixed(2)} ÷{" "}
                            {useRate.toFixed(2)} = <strong>{autoGrams}g</strong>
                          </small>
                        )}
                      </div>
                    );
                  })()}

                {closeReturnType === "silver" &&
                  (() => {
                    const sRate = closeChitData.silverRate || todaySilverRate;
                    const autoGrams =
                      sRate > 0
                        ? (closeChitData.totalPaid / sRate).toFixed(3)
                        : "0.000";
                    return (
                      <div className="mb-2">
                        <label className="form-label">
                          Silver Weight to Return (g)
                          {sRate > 0 && (
                            <span className="text-muted ms-2 small">
                              @ ₹{sRate.toFixed(2)}/g
                            </span>
                          )}
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Silver weight in grams"
                          value={closeSilverWeight}
                          onChange={(e) => setCloseSilverWeight(e.target.value)}
                        />
                        {sRate > 0 && (
                          <small className="text-muted">
                            Silver rate: ₹{sRate.toFixed(2)}/g —{" "}
                            {closeChitData.totalPaid.toFixed(2)} ÷{" "}
                            {sRate.toFixed(2)} = <strong>{autoGrams}g</strong>
                          </small>
                        )}
                      </div>
                    );
                  })()}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => setCloseChitModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-warning btn-sm"
                  onClick={handleCloseChitConfirm}
                  disabled={
                    closeLoading ||
                    (closeReturnType === "gold" && !closeGoldWeight) ||
                    (closeReturnType === "silver" && !closeSilverWeight)
                  }
                >
                  {closeLoading ? "Closing..." : "Confirm Close Chit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showModal ||
        updateModal ||
        deleteShowModals ||
        paymentModal ||
        infoModal ||
        closeChitModal) && <div className="modal-backdrop fade show"></div>}
    </>
  );
}

export default ChitList;
